import {FC, useEffect, useState} from 'react';
import {Box, Typography} from '@mui/material';
import Button from '@components/Button';
import {ERROR, H5, IMAGE_TABLE_ALT} from '@/utils/constants';
import Modal from '@/shared/components/Modal';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
import {useAppSelector} from '@/store/hooks';
import Loader from '@/shared/components/Loader';
import ChangePasswordModal from '@/pages/Organization/Profile/General/ChangePasswordModal/index';
import TwoStepAuthentication from './TwoStepAuthentication';
import TwoStepAuthenticationDisable from './TwoStepAuthenticationDisable';
import {getSuperAdminSelector} from '@/store/admin';
import ProfileGeneralForm from './ProfileForm';
import adminIcon from '@images/admin-logo.png';
import {profileFormStyle} from './ProfileForm/ProfileFormStyle.styles';
import {useApiInstance} from '@/contexts/ApiInstanceContext';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {SuperAdminService} from '@/utils/services';

const AdminProfileGeneral: FC = () => {
  const [isShowDeleteAccountModal, setIsShowDeleteAccountModal] = useState(false);
  const [isShowChangePasswordModal, setIsShowChangePasswordModal] = useState(false);
  const [isShowAuthenticationModal, setIsShowAuthenticationModal] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const handleDeleteAccountModalChange = () => setIsShowDeleteAccountModal(!isShowDeleteAccountModal);
  const handleChangePasswordModalChange = () => setIsShowChangePasswordModal(!isShowChangePasswordModal);
  //   const handleChangeAuthenticationModalChange = () => setIsShowAuthenticationModal(!isShowAuthenticationModal);

  const admin = useAppSelector(getSuperAdminSelector);
  const {apiInstance} = useApiInstance();

  const deleteAccount = () => {
    handleDeleteAccountModalChange();
  };

  const handleChangeAuthenticationModalChange = () => {
    const willClose = isShowAuthenticationModal;
    setIsShowAuthenticationModal(!isShowAuthenticationModal);
    if (willClose) getAdmin2FactorStatus();
  };

  const getAdmin2FactorStatus = async () => {
    try {
      const {data} = await SuperAdminService.getAdmin2FactorStatus();
      setIsVerified(data.isEnabled);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    getAdmin2FactorStatus();
  }, []);

  return (
    <Box sx={profileFormStyle.wrapper}>
      <Box sx={profileFormStyle.avatarImage}>
        <img src={adminIcon} alt={IMAGE_TABLE_ALT} />
      </Box>
      {admin ? (
        <ProfileGeneralForm admin={admin} />
      ) : (
        <Box sx={profileFormStyle.loaderWrap}>
          <Loader size={50} />
        </Box>
      )}

      <Box sx={profileFormStyle.widget}>
        <Box>
          <Typography variant={H5} sx={profileFormStyle.widgetTitle}>
            Password
          </Typography>
          <Typography sx={profileFormStyle.secondaryText}>You can change your password at any time.</Typography>
        </Box>
        <Box>
          <Button
            variant="text"
            sx={profileFormStyle.btn}
            onClick={handleChangePasswordModalChange}
            title="Change Password"
          />
        </Box>
      </Box>
      <Box sx={profileFormStyle.widget}>
        <Box sx={profileFormStyle.twoStep}>
          <Typography variant={H5} sx={profileFormStyle.widgetTitle}>
            Security
          </Typography>
          <Typography sx={profileFormStyle.secondaryText}>
            If two-step verification is enabled, you will be asked to enter an additional code when you sign in from an
            unrecognized device. This protects your account.
          </Typography>
        </Box>
        <Box>
          <Button
            variant="text"
            sx={profileFormStyle.btn}
            title={isVerified ? 'Disable Two-Step Authentication' : 'Enable Two-Step Authentication'}
            onClick={handleChangeAuthenticationModalChange}
          />
        </Box>
      </Box>

      <Modal
        closeButtonTitle="Cancel"
        mainButtonTitle="Delete"
        onClose={handleDeleteAccountModalChange}
        onSave={deleteAccount}
        open={isShowDeleteAccountModal}
        sx={profileFormStyle.modal}
        title="Delete Account"
        description="Are you sure you want to delete?"
        mainIcon={<DangerIcon />}
        color={ERROR}
        isShowCloseButton
        isShowSaveButton
      />

      <Modal
        onClose={handleChangePasswordModalChange}
        open={isShowChangePasswordModal}
        sx={profileFormStyle.customeModal}
      >
        <ChangePasswordModal onCloseModal={handleChangePasswordModalChange} apiInstance={apiInstance} />
      </Modal>

      {isShowAuthenticationModal && (
        <Modal
          onClose={handleChangeAuthenticationModalChange}
          open={isShowAuthenticationModal}
          sx={profileFormStyle.modal}
        >
          {isVerified ? (
            <TwoStepAuthenticationDisable onCloseModal={handleChangeAuthenticationModalChange} />
          ) : (
            <TwoStepAuthentication onCloseModal={handleChangeAuthenticationModalChange} />
          )}
        </Modal>
      )}
    </Box>
  );
};

export default AdminProfileGeneral;
